/**
 * PolicyAttachModal - Attach Policy to Datasets
 *
 * Modal for attaching a policy to multiple datasets with priority setting.
 * Shows current attachments and allows batch attach/detach operations.
 */

import RefreshOverlay from '@/components/common/RefreshOverlay';
import ServerSearchableDropdown from '@/components/common/ServerSearchableDropdown';
import { apiClient, Dataset, PaginatedResponse } from '@/services/apiClient';
import type { Policy } from '@/types/policy';
import { getErrorMessage } from '@/utils/errorHandling';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface PolicyAttachModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  policy: Policy | null;
}

export default function PolicyAttachModal({
  show,
  onHide,
  onSuccess,
  policy,
}: PolicyAttachModalProps) {
  const { t } = useTranslation();

  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [selectedDatasetIds, setSelectedDatasetIds] = useState<number[]>([]);
  const [priority, setPriority] = useState(0);
  const [attachedDatasetIds, setAttachedDatasetIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAttaching, setIsAttaching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshingDatasets, setIsRefreshingDatasets] = useState(false);

  // Refresh overlay state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<string>('');

  // Handle refresh with overlay
  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    try {
      setRefreshStatus('datasets');
      const datasetsResponse = await apiClient.getDatasets();
      if (datasetsResponse.data?.datasets) {
        setDatasets(datasetsResponse.data.datasets);
      }

      if (policy) {
        setRefreshStatus('attachments');
        const attachedResponse = await apiClient.getPolicyDatasets(policy.id);
        if (attachedResponse.data) {
          const datasetIds = attachedResponse.data as number[];
          setAttachedDatasetIds(datasetIds);
        }
      }

      toast.success('Data refreshed successfully');
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || 'Failed to refresh data';
      toast.error(errorMessage);
    } finally {
      setIsRefreshing(false);
      setRefreshStatus('');
    }
  };

  // Load datasets and current attachments
  const loadData = useCallback(async () => {
    if (!policy) return;

    setIsLoading(true);
    setError(null);

    try {
      // Load all datasets
      const datasetsResponse = await apiClient.getDatasets();
      if (datasetsResponse.data?.datasets) {
        setDatasets(datasetsResponse.data.datasets);
      }

      // Load datasets this policy is already attached to
      const attachedResponse = await apiClient.getPolicyDatasets(policy.id);
      if (attachedResponse.data) {
        const datasetIds = attachedResponse.data as number[];
        setAttachedDatasetIds(datasetIds);
      }

      setIsLoading(false);
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to load datasets');
      setIsLoading(false);
    }
  }, [policy]);

  // Load datasets and current attachments when modal opens
  useEffect(() => {
    if (show && policy) {
      loadData();
    }
  }, [show, policy, loadData]);

  const handleAttach = async () => {
    if (!policy || selectedDatasetIds.length === 0) return;

    setIsAttaching(true);
    setError(null);

    try {
      const response = await apiClient.attachPolicyToDatasets(policy.id, {
        dataset_ids: selectedDatasetIds,
        priority: priority,
      });

      if (response.error) {
        toast.error(response.error);
        setError(response.error);
        setIsAttaching(false);
        return;
      }

      // Show success/failure feedback
      const result = response.data as { successful?: number[]; failed?: number[] } | undefined;
      if (result?.successful && result.successful.length > 0) {
        toast.success(
          t(
            'admin.policyManagement.attachedToDatasets',
            `Attached to ${result.successful.length} dataset(s)`
          )
        );
      }

      if (result?.failed && result.failed.length > 0) {
        toast(
          t(
            'admin.policyManagement.attachFailed',
            `Failed to attach to ${result.failed.length} dataset(s)`
          ),
          { icon: '⚠️' }
        );
      }

      // Reset selection
      setSelectedDatasetIds([]);
      setPriority(0);

      setIsAttaching(false);
      onSuccess();
      loadData(); // Reload to show updated attachments
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || 'Failed to attach policy';
      toast.error(errorMessage);
      setError(errorMessage);
      setIsAttaching(false);
    }
  };

  const handleDetach = async (datasetId: number) => {
    if (!policy) return;

    try {
      const response = await apiClient.detachPolicyFromDatasets(policy.id, {
        dataset_ids: [datasetId],
      });

      if (response.error) {
        toast.error(response.error);
        return;
      }

      toast.success(t('admin.policyManagement.detachedFromDataset', 'Detached from dataset'));
      onSuccess();
      loadData(); // Reload to show updated attachments
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || 'Failed to detach policy';
      toast.error(errorMessage);
    }
  };

  const handleSelectDataset = (value: string) => {
    const datasetId = parseInt(value);
    if (datasetId && !selectedDatasetIds.includes(datasetId)) {
      setSelectedDatasetIds([...selectedDatasetIds, datasetId]);
    }
  };

  const handleRefreshDatasets = async () => {
    setIsRefreshingDatasets(true);
    try {
      const datasetsResponse = await apiClient.getDatasets();
      if (datasetsResponse.data?.datasets) {
        setDatasets(datasetsResponse.data.datasets);
        toast.success('Datasets refreshed');
      }
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || 'Failed to refresh datasets';
      toast.error(errorMessage);
    } finally {
      setIsRefreshingDatasets(false);
    }
  };

  const handleRemoveSelection = (datasetId: number) => {
    setSelectedDatasetIds(selectedDatasetIds.filter((id) => id !== datasetId));
  };

  const getDatasetName = (datasetId: number): string => {
    const dataset = datasets.find((d) => d.id === datasetId);
    return dataset?.name || `Dataset #${datasetId}`;
  };

  // Fetch datasets for ServerSearchableDropdown (excludes already attached)
  const fetchDatasets = useCallback(async (search: string, page: number, perPage: number) => {
    const response = await apiClient.searchAdminDatasets(search, page, perPage);
    if (response.error) {
      throw new Error(response.error);
    }
    const data = response.data as PaginatedResponse<Dataset>;
    // Filter out already attached datasets
    const filteredItems = data.items.filter((d) => !attachedDatasetIds.includes(d.id));
    return {
      ...data,
      items: filteredItems,
    };
  }, [attachedDatasetIds]);

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <div className="d-flex justify-content-between align-items-center w-100 me-3">
          <Modal.Title>
            {t('admin.policyManagement.attachToDatasets', 'Attach Policy to Datasets')}
          </Modal.Title>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleRefreshAll}
            disabled={isRefreshing}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh
          </Button>
        </div>
      </Modal.Header>

      <Modal.Body style={{ position: 'relative' }}>
        <RefreshOverlay isRefreshing={isRefreshing} currentItem={refreshStatus} />
        {policy && (
          <Alert variant="info">
            <strong>{t('admin.policyManagement.policy', 'Policy')}:</strong> {policy.name}
            {policy.description && (
              <div className="small mt-1 text-muted">{policy.description}</div>
            )}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Dataset Selection */}
        <h6>{t('admin.policyManagement.selectDatasets', 'Select Datasets to Attach')}</h6>

        <Form.Group className="mb-3">
          <Form.Label>{t('admin.policyManagement.datasets', 'Datasets')}</Form.Label>
          <ServerSearchableDropdown<Dataset>
            key={attachedDatasetIds.join(',')}
            fetchData={fetchDatasets}
            value=""
            onChange={handleSelectDataset}
            getOptionValue={(d) => d.id.toString()}
            getOptionLabel={(d) => d.name || `Dataset #${d.id}`}
            placeholder={t('admin.policyManagement.selectDataset', 'Select a dataset...')}
            disabled={isLoading}
            onRefresh={handleRefreshDatasets}
            isRefreshing={isRefreshingDatasets}
          />
        </Form.Group>

        {/* Selected Datasets */}
        {selectedDatasetIds.length > 0 && (
          <div className="mb-3">
            <strong>{t('admin.policyManagement.selectedDatasets', 'Selected Datasets')}:</strong>
            <div className="mt-2">
              {selectedDatasetIds.map((id) => (
                <Badge key={id} bg="primary" className="me-2 mb-2">
                  {getDatasetName(id)}
                  <Button
                    size="sm"
                    variant="link"
                    className="text-white p-0 ms-2"
                    onClick={() => handleRemoveSelection(id)}
                  >
                    ×
                  </Button>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Priority */}
        <Form.Group className="mb-4">
          <Form.Label>{t('admin.policyManagement.priority', 'Priority')}</Form.Label>
          <Form.Control
            type="number"
            value={priority}
            onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
            min={-1000}
            max={1000}
          />
          <Form.Text className="text-muted">
            {t(
              'admin.policyManagement.priorityHelp',
              'Higher priority policies are evaluated first (range: -1000 to 1000, default: 0). Deny always wins regardless of priority.'
            )}
          </Form.Text>
        </Form.Group>

        {/* Attach Button */}
        {selectedDatasetIds.length > 0 && (
          <Button
            variant="primary"
            onClick={handleAttach}
            disabled={isAttaching}
            className="mb-4 w-100"
          >
            {isAttaching
              ? t('admin.policyManagement.attaching', 'Attaching...')
              : t('admin.policyManagement.attach', `Attach to ${selectedDatasetIds.length} Dataset(s)`)}
          </Button>
        )}

        {/* Current Attachments */}
        <hr />
        <h6>{t('admin.policyManagement.currentAttachments', 'Currently Attached Datasets')}</h6>

        {attachedDatasetIds.length === 0 ? (
          <Alert variant="info" className="mb-0">
            {t(
              'admin.policyManagement.noAttachments',
              'This policy is not attached to any datasets yet.'
            )}
          </Alert>
        ) : (
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>#</th>
                <th>{t('admin.policyManagement.dataset', 'Dataset')}</th>
                <th>{t('admin.policyManagement.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {attachedDatasetIds.map((id, idx) => (
                <tr key={id}>
                  <td>{idx + 1}</td>
                  <td>{getDatasetName(id)}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDetach(id)}
                    >
                      {t('admin.policyManagement.detach', 'Detach')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('admin.policyManagement.close', 'Close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
